/**
 * iter-170ai — Accountant Link manager.
 *
 * Lets a user mint a read-only share-able link for their accountant,
 * list existing links (with last-used timestamps), and revoke them.
 * The URL is shown right after creation with a copy-to-clipboard CTA;
 * since the token is the link itself, we surface it on every list
 * row too — unlike API keys, there's no "shown once" constraint
 * because the value IS the URL the user needs to keep sharing.
 *
 * Lives inside ``<LockedSection featureKey="accountant_readonly">``.
 */
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { Eye, Plus, Copy, Trash2, Loader2, ExternalLink } from "lucide-react";
import { useLang } from "../contexts/LangContext";
import { tx } from "../i18n";
import { api } from "../lib/api";
import { toast } from "sonner";

function fmtDate(iso, lang) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(
      lang === "es" ? "es-CO" : lang === "pt" ? "pt-BR" : "en-US",
      { year: "numeric", month: "short", day: "2-digit" },
    );
  } catch { return iso; }
}

function buildUrl(token) {
  // Use window.location.origin so the link reflects the actual
  // hostname the user is viewing the SPA from (prod / preview).
  return `${window.location.origin}/r/${token}`;
}

export function AccountantLinkCard() {
  const { lang } = useLang();
  const qc = useQueryClient();
  const [label, setLabel] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["accountant-links"],
    queryFn: async () => (await api.get("/accountant-links")).data,
    retry: false,
  });
  const items = data?.items || [];

  const createMut = useMutation({
    mutationFn: async () =>
      (await api.post("/accountant-links", { label: label.trim() })).data,
    onSuccess: (d) => {
      setLabel("");
      qc.invalidateQueries({ queryKey: ["accountant-links"] });
      // Auto-copy the new URL — saves a second click; users almost
      // always want this URL in their clipboard right after creation.
      const url = buildUrl(d.token);
      navigator.clipboard?.writeText(url).then(
        () => toast.success(tx(lang, "Link created — URL copied",
          "Link creado — URL copiada",
          "Link criado — URL copiada")),
        () => toast.success(tx(lang, "Link created",
          "Link creado",
          "Link criado")),
      );
    },
    onError: () => toast.error(tx(lang, "Create failed",
      "Error al crear", "Erro ao criar")),
  });

  const revokeMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/accountant-links/${id}`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["accountant-links"] });
      toast.success(tx(lang, "Link revoked",
        "Link revocado", "Link revogado"));
    },
    onError: () => toast.error(tx(lang, "Revoke failed",
      "Error al revocar", "Erro ao revogar")),
  });

  const copyUrl = async (token) => {
    try {
      await navigator.clipboard.writeText(buildUrl(token));
      toast.success(tx(lang, "Copied", "Copiado", "Copiado"));
    } catch {
      toast.error(tx(lang, "Copy failed", "No se pudo copiar", "Falha ao copiar"));
    }
  };

  return (
    <Card className="p-5 rounded-2xl space-y-4" data-testid="accountant-link-card">
      <div className="flex items-center gap-2">
        <Eye className="h-4 w-4 text-slate-700" />
        <h3 className="font-semibold text-slate-900">
          {tx(lang, "Read-only accountant links",
            "Links solo-lectura para contador",
            "Links somente leitura para contador")}
        </h3>
      </div>

      <p className="text-xs text-slate-600 leading-snug">
        {tx(lang,
          "Generate a unique URL your accountant can use to view your reports, movements, debts and cash accounts — no edit access, no login required. Revocable anytime.",
          "Genera una URL única para que tu contador vea tus reportes, movimientos, deudas y cuentas — sin permiso de edición, sin login. Revocable cuando quieras.",
          "Gere uma URL única para seu contador acessar seus relatórios, movimentos, dívidas e contas — sem permissão de edição, sem login. Revogável quando quiser.")}
      </p>

      <div className="flex gap-2">
        <Input
          placeholder={tx(lang, "Link label (e.g. 'Accountant March 2026')",
            "Etiqueta del link (ej: 'Contador Marzo 2026')",
            "Rótulo do link (ex: 'Contador Março 2026')")}
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          maxLength={80}
          data-testid="accountant-link-new-label"
          className="flex-1"
        />
        <Button
          onClick={() => createMut.mutate()}
          disabled={!label.trim() || createMut.isPending}
          className="gap-1.5"
          data-testid="accountant-link-create-btn"
        >
          {createMut.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
          {tx(lang, "Create", "Crear", "Criar")}
        </Button>
      </div>

      {isLoading ? (
        <p className="text-xs text-slate-400 italic">
          {tx(lang, "Loading…", "Cargando…", "Carregando…")}
        </p>
      ) : items.length === 0 ? (
        <p className="text-xs text-slate-500 italic py-6 text-center" data-testid="accountant-link-empty">
          {tx(lang, "No links yet.", "Aún no tienes links.", "Ainda sem links.")}
        </p>
      ) : (
        <ul className="space-y-2" data-testid="accountant-link-list">
          {items.map((l) => {
            const isRevoked = !!l.revoked_at;
            const url = l.token ? buildUrl(l.token) : null;
            return (
              <li
                key={l.id}
                className={`p-3 rounded-xl border ${
                  isRevoked ? "border-slate-200 bg-slate-50 opacity-70" : "border-slate-200 bg-white"
                }`}
                data-testid={`accountant-link-row-${l.id}`}
              >
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <span className="text-sm font-semibold text-slate-900 truncate">{l.label}</span>
                    {isRevoked && (
                      <Badge variant="destructive" className="text-[10px]">
                        {tx(lang, "Revoked", "Revocado", "Revogado")}
                      </Badge>
                    )}
                  </div>
                  {!isRevoked && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => revokeMut.mutate(l.id)}
                      disabled={revokeMut.isPending}
                      className="gap-1.5 rounded-full text-rose-700 border-rose-200 hover:bg-rose-50"
                      data-testid={`accountant-link-revoke-${l.id}`}
                    >
                      <Trash2 className="h-3 w-3" />
                      {tx(lang, "Revoke", "Revocar", "Revogar")}
                    </Button>
                  )}
                </div>
                {!isRevoked && url && (
                  <div className="flex items-center gap-2 mb-1.5">
                    <code className="flex-1 text-[11px] font-mono bg-slate-50 border border-slate-200 px-2 py-1 rounded truncate">
                      {url}
                    </code>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => copyUrl(l.token)}
                      className="h-7 px-2 gap-1"
                      data-testid={`accountant-link-copy-${l.id}`}
                    >
                      <Copy className="h-3 w-3" />
                    </Button>
                    <a href={url} target="_blank" rel="noreferrer">
                      <Button size="sm" variant="ghost" className="h-7 px-2 gap-1">
                        <ExternalLink className="h-3 w-3" />
                      </Button>
                    </a>
                  </div>
                )}
                <p className="text-[10px] text-slate-400">
                  {tx(lang, "Created", "Creado", "Criado")} {fmtDate(l.created_at, lang)}
                  {l.last_used_at && (
                    <> · {tx(lang, "Last accessed", "Último acceso", "Último acesso")} {fmtDate(l.last_used_at, lang)}</>
                  )}
                </p>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}
